/* eslint-disable */
import React, { useEffect, useState } from "react";
import useUserContext from "../../hooks/useUserContext";
import getOfficerndComparison from "./getOfficerndComparison";
import markComparisonDone from "./markComparisonDone";
import approveComparison from "./approveComparison"; // <-- import approve API
import notify from "../../services/toast";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ComparisonModal from "../../components/salesforce-officernd/ComparisonModal";
import { ADMINTYPE } from "../../constant";

const LEVEL_BY_STATUS = {
  red: 1,
  yellow: 2,
  purple: 3,
  green: 0
};

const LEVEL_TO_COLOR = {
  0: "bg-green-100",
  1: "bg-red-100",
  2: "bg-yellow-100",
  3: "bg-purple-200"
};

function OfficerndComparisonPage() {
  const { userState } = useUserContext();

  const [companies, setCompanies] = useState([]);
  const [totals, setTotals] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [updatedRows, setUpdatedRows] = useState({});
  const [approvedRows, setApprovedRows] = useState({});
  const [approving, setApproving] = useState({});
  const [filters, setFilters] = useState({ market: "", location: "" });
  const [tableSearch, setTableSearch] = useState("");
  const [statsMarket, setStatsMarket] = useState("");

  const [availableMarkets, setAvailableMarkets] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);

  const [globalStats, setGlobalStats] = useState({});
  const [statsByMarket, setStatsByMarket] = useState({});
  const [levelMeaning, setLevelMeaning] = useState({});

  const isSuperAdmin = userState?.role?.includes(ADMINTYPE.SUPERADMIN);

  /* -------------------- Helpers -------------------- */
  const getWorstLevelFromFields = (fields) => {
    let worst = 0;
    Object.values(fields || {}).forEach(f => {
      const lvl = LEVEL_BY_STATUS[f.status] || 0;
      if (lvl > worst) worst = lvl;

      if (f.fields) {
        const nested = getWorstLevelFromFields(f.fields);
        if (nested > worst) worst = nested;
      }
    });
    return worst;
  };

  const getCompanyRowLevel = (company) => {
    let worst = getWorstLevelFromFields(company.company.fields);

    company.members.forEach(m => {
      const lvl = getWorstLevelFromFields(m.fields);
      if (lvl > worst) worst = lvl;
    });

    company.opportunities.forEach(o => {
      const lvl = getWorstLevelFromFields(o.fields);
      if (lvl > worst) worst = lvl;
    });

    return worst;
  };

  const getRowColor = (company) => {
    if (approvedRows[company.referenceId]) return "bg-primary-100";
    if (updatedRows[company.referenceId] === "highlight") return "bg-yellow-200";
    if (updatedRows[company.referenceId]) return "bg-primary-400 text-white";

    const level = company.rowLevel ?? 0;
    return LEVEL_TO_COLOR[level] || "bg-green-100";
  };

  /* -------------------- Fetch -------------------- */
  const fetchData = async (p = 1) => {
    if (!userState?.accessToken) return;
    setIsLoading(true);

    try {
      const res = await getOfficerndComparison(
        userState.accessToken,
        p,
        limit,
        { ...filters, search: tableSearch }
      );

      const rows = res?.data?.companies || [];

      setGlobalStats(res?.data?.stats?.all || {});
      setStatsByMarket(res?.data?.stats?.byMarket || {});

      setCompanies(rows);
      setTotals(res?.count?.companies || 0);
      setPage(res.page || 1);

      // DONE map
      const doneMap = {};
      const approvedMap = {};
      rows.forEach(r => {
        if (r.done) doneMap[r.referenceId] = true;
        if (r.doneBySuperAdmin?.isDone) approvedMap[r.referenceId] = true;
      });
      setUpdatedRows(doneMap);
      setApprovedRows(approvedMap);

      if (res?.data?.availableMarkets) setAvailableMarkets(res.data.availableMarkets);
      if (res?.data?.availableLocations) setAvailableLocations(res.data.availableLocations);

      setGlobalStats(res?.data?.stats?.all || {});
      setStatsByMarket(res?.data?.stats?.byMarket || {});
      setLevelMeaning(res?.data?.levelMeaning || {});

    } catch (err) {
      console.error(err);
      notify("Error fetching comparison data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, [userState?.accessToken, filters, tableSearch]);

  /* -------------------- Modal -------------------- */
  const openModal = (company) => {
    setSelectedCompany(company);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedCompany(null);
    setIsModalOpen(false);
  };

  /* -------------------- DONE -------------------- */
  const markUpdated = async (item) => {
    try {
      const body = {
        type: item.type,
        referenceId: item.referenceId,
        rowData: item,
        adminId: userState.id
      };

      const res = await markComparisonDone(userState.accessToken, body);

      if (res.status === "success") {
        notify("Marked as done!", "success");

        setUpdatedRows(prev => ({
          ...prev,
          [item.referenceId]: "highlight"
        }));

        setTimeout(() => {
          setUpdatedRows(prev => ({
            ...prev,
            [item.referenceId]: true
          }));
        }, 400);
      }
    } catch {
      notify("Failed to mark done", "error");
    }
  };

  /* -------------------- APPROVE -------------------- */
  const approveUpdated = async (item) => {
    const isSuperAdmin = userState?.role?.includes(ADMINTYPE.SUPERADMIN);

    if (!isSuperAdmin) {
      notify("You are not a Super Admin", "error");
      return;
    }

    try {
      const body = {
        referenceId: item.referenceId,
        adminId: userState.id  // make sure backend knows who is approving
      };

      const res = await approveComparison(userState.accessToken, body);

      if (res.status === "success") {
        notify("Approved by Super Admin!", "success");
        setApprovedRows(prev => ({ ...prev, [item.referenceId]: true }));
      } else {
        notify(res.message || "Approval failed", "error");
      }
    } catch (err) {
      console.error("Approve error:", err);
      notify("Failed to approve", "error");
    }
  };

  /* -------------------- Pagination -------------------- */
  const totalPages = Math.ceil(totals / limit);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleStatsMarketChange = (e) => {
    setStatsMarket(e.target.value);
  };

  const clearFilters = () => {
    setFilters({ market: "", location: "" });
    setTableSearch("");
    setPage(1);
  };

  const filteredCompanies = companies.filter(company => {
    if (!tableSearch) return true;
    const searchLower = tableSearch.toLowerCase();
    const name = (company.company.salesforce?.name || company.company.officernd?.name || "").toLowerCase();
    const email = (company.company.salesforce?.primaryEmail || company.company.officernd?.email || "").toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  });

  const displayedStats = statsMarket ? statsByMarket[statsMarket] || {} : globalStats;

  /* -------------------- Render -------------------- */
  return (
    <div className="bg-gray-100 rounded-3xl shadow-xl p-6 sm:p-8 space-y-8">
      <h2 className="text-3xl font-semibold mb-6">Salesforce ↔ OfficeRnD Comparison</h2>

      {/* Stats Market Filter */}
      <div className="mb-4 flex gap-4 items-end">
        <div>
          <label className="block mb-1 font-medium">Stats Market</label>
          <select
            value={statsMarket}
            onChange={handleStatsMarketChange}
            className="border px-8 py-1 rounded"
          >
            <option value="">All Markets</option>
            {availableMarkets.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Panel */}
      <div className="mb-4 p-4 bg-gray-50 rounded border">
        <h3 className="font-semibold mb-2 text-lg">
          Stats {statsMarket ? `(${statsMarket})` : "(All Markets)"}
        </h3>

        <div className="grid grid-cols-4 gap-4 text-sm">
          {[0, 1, 2, 3].map(level => {
            const count = displayedStats[`level${level}`] || 0;
            const color = LEVEL_TO_COLOR[level] || "bg-green-100";
            const meaning = levelMeaning[level] || `Level ${level}`;

            return (
              <div
                key={level}
                className={`${color} p-3 rounded text-center cursor-default relative`}
                title={meaning}
              >
                <div className="font-bold text-xl">{count}</div>
                <div className="mt-1">{meaning}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-4 items-end">
        <div className="flex flex-col">
          <label htmlFor="market" className="text-sm font-medium mb-1">Market</label>
          <select
            id="market"
            name="market"
            value={filters.market}
            onChange={handleFilterChange}
            className="border rounded px-3 py-2 w-48 focus:ring-2 focus:ring-success-800 focus:outline-none shadow-sm hover:border-gray-400"
          >
            <option value="">All Markets</option>
            {availableMarkets.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="location" className="text-sm font-medium mb-1">Location</label>
          <select
            id="location"
            name="location"
            value={filters.location}
            onChange={handleFilterChange}
            className="border rounded px-3 py-2 focus:ring-2 focus:ring-success-800 focus:outline-none shadow-sm hover:border-gray-400"
          >
            <option value="">All Locations</option>
            {availableLocations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col relative">
          <label htmlFor="search" className="text-sm font-medium mb-1">Search</label>
          <div className="relative w-64">
            <input
              id="search"
              type="text"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Company name or email"
              className="border rounded px-3 py-2 pl-10 w-full focus:ring-2 focus:ring-success-800 focus:outline-none shadow-sm hover:border-gray-400"
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              🔍
            </span>
          </div>
        </div>

        <button
          onClick={clearFilters}
          className="flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded transition-all shadow-sm"
        >
          <span className="mr-2">✖</span> Clear
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <Skeleton count={10} height={40} />
      ) : companies.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No companies found.</p>
      ) : (
        <table className="min-w-full table-auto border border-gray-300 rounded">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="p-3 text-left">Source</th>
              <th className="p-3 text-left">Company Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Market</th>
              <th className="p-3 text-left">Location</th>
              <th className="p-3 text-center">Actions</th> {/* <-- Actions */}
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.map(company => {
              const sf = company.company.salesforce || {};
              const ornd = company.company.officernd || {};

              const isDone = updatedRows[company.referenceId];
              const isApproved = approvedRows[company.referenceId];

              return (
                <tr
                  key={company.referenceId}
                  className={`cursor-pointer border-b ${getRowColor(company)}`}
                >
                  <td className="p-3 capitalize font-medium">{company.source}</td>
                  <td className="p-3" onClick={() => openModal(company)}>
                    {sf.name || ornd.name || "-"}
                  </td>
                  <td className="p-3" onClick={() => openModal(company)}>
                    {sf.primaryEmail || ornd.email || "-"}
                  </td>
                  <td className="p-3">{company.company.fields.market?.value || "-"}</td>
                  <td className="p-3">{company.company.fields.location?.value || "-"}</td>
                  <td className="p-3 text-center flex gap-2 justify-center">
                    {/* Mark Done */}
                    {!isDone && (
                      <button
                        onClick={() => markUpdated(company)}
                        className="px-3 py-1 bg-primary-400 text-white rounded hover:bg-primary-100"
                      >
                        Done
                      </button>
                    )}
                    {isDone && !isApproved && (
                      <button
                        onClick={() => approveUpdated(company)}
                        disabled={!userState?.role?.includes(ADMINTYPE.SUPERADMIN)}
                        className={`px-3 py-1 rounded text-white ${userState?.role?.includes(ADMINTYPE.SUPERADMIN)
                            ? "bg-success-800 hover:bg-success-50"
                            : "bg-gray-400 cursor-not-allowed"
                          }`}
                      >
                        Approve
                      </button>
                    )}
                    {isApproved && <span className="font-bold text-success-800">✔ Approved </span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      <div className="flex justify-between mt-4">
        <button
          disabled={page <= 1}
          onClick={() => fetchData(page - 1)}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>Page {page} / {totalPages}</span>
        <button
          disabled={page >= totalPages}
          onClick={() => fetchData(page + 1)}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && selectedCompany && (
        <ComparisonModal
          isOpen={isModalOpen}
          onClose={closeModal}
          item={selectedCompany}
        />
      )}
    </div>
  );
}

export default OfficerndComparisonPage;
